import React from 'react';
import styles from './css/index.module.css';
import { Input } from '@fluentui/react-components';
import { useDispatch, useSelector } from 'react-redux';
import { format, differenceInCalendarDays } from 'date-fns';
import { AiOutlineLoading3Quarters } from 'react-icons/ai';
import { Edit16Regular, Delete16Regular } from '@fluentui/react-icons';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
import { PatchProjectData } from '../../redux/projectDataSlice';

const AddMilestones = () => {
    const dispatch = useDispatch();
    const { selectedProject, projectDetailButtonLoading } = useSelector(state => state.projectData);

    const [milestones, setMilestones] = React.useState([]);
    const [milestoneDraft, setMilestoneDraft] = React.useState({
        milestoneName: '',
        milestoneDate: '',
    });
    const [editingIndex, setEditingIndex] = React.useState(null);

    React.useEffect(() => {
        if (selectedProject?.project_milestones && Array.isArray(selectedProject.project_milestones)) {
            setMilestones(selectedProject.project_milestones);
        } else {
            setMilestones([]);
        }
    }, [selectedProject]);

    const handleDraftChange = (field, value) => {
        setMilestoneDraft(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const resetDraft = () => {
        setMilestoneDraft({
            milestoneName: '',
            milestoneDate: '',
        });
        setEditingIndex(null);
    };

    const persistMilestones = updated => {
        if (!selectedProject?.id) return;
        dispatch(
            PatchProjectData({
                data: { project_milestones: updated },
                id: selectedProject.id,
            }),
        );
    };

    const handleSave = () => {
        const name = milestoneDraft.milestoneName?.trim();
        const date = milestoneDraft.milestoneDate;
        if (!name || !date) return;

        const existing = [...milestones];
        if (editingIndex !== null && editingIndex >= 0 && editingIndex < existing.length) {
            existing[editingIndex] = {
                ...existing[editingIndex],
                milestoneName: name,
                milestoneDate: date,
            };
        } else {
            existing.push({
                milestoneName: name,
                milestoneDate: date,
                created_at: new Date().toISOString(),
            });
        }

        setMilestones(existing);
        persistMilestones(existing);
        resetDraft();
    };

    const handleEdit = index => {
        const current = milestones[index];
        if (!current) return;
        setMilestoneDraft({
            milestoneName: current.milestoneName || '',
            milestoneDate: current.milestoneDate || '',
        });
        setEditingIndex(index);
    };

    const handleDelete = index => {
        const updated = milestones.filter((_, idx) => idx !== index);
        setMilestones(updated);
        persistMilestones(updated);
        if (editingIndex === index) {
            resetDraft();
        }
    };

    const formatMilestoneDate = dateStr => {
        if (!dateStr) return '--';
        try {
            return format(new Date(dateStr), 'dd/MM/yyyy');
        } catch {
            return dateStr;
        }
    };

    const getDaysAgoLabel = dateStr => {
        if (!dateStr) return '';
        try {
            const diff = differenceInCalendarDays(new Date(), new Date(dateStr));
            if (Number.isNaN(diff)) return '';
            if (diff === 0) return 'Today';
            if (diff > 0) return `${diff} day${diff === 1 ? '' : 's'} ago`;
            const abs = Math.abs(diff);
            return `In ${abs} day${abs === 1 ? '' : 's'}`;
        } catch {
            return '';
        }
    };

    return (
        <div className={styles.componentCard}>
            <h5 className={styles.componentTitle + ' mt-0'}>Milestones</h5>

            {milestones.length > 0 ? (
                <div className="mb-4">
                    {milestones.map((m, index) => (
                        <div key={index} className="d-flex align-items-start justify-content-between mb-3">
                            <div>
                                <p className={styles.mileStoneName}>{m.milestoneName || '--'}</p>
                                <p className={styles.mileStoneHelperText}>
                                    {formatMilestoneDate(m.milestoneDate)}{' '}
                                    <i style={{ marginLeft: 16, color: 'var(--Neutral-Gray-6, #989BAD)' }}>{getDaysAgoLabel(m.milestoneDate)}</i>
                                </p>
                            </div>
                            <div className="d-flex align-items-center" style={{ gap: 8 }}>
                                <button
                                    type="button"
                                    onClick={() => handleEdit(index)}
                                    style={{
                                        border: 'none',
                                        background: 'transparent',
                                        padding: 0,
                                        cursor: 'pointer',
                                    }}>
                                    <Edit16Regular color="var(--Neutral-Gray-6, #989BAD)" />
                                </button>
                                <DoubleConfirmationModal
                                    message="Deleting a Milestone permanently removes it from the Project, and this is irreversible."
                                    handleAction={() => handleDelete(index)}>
                                    <button
                                        type="button"
                                        style={{
                                            border: 'none',
                                            background: 'transparent',
                                            padding: 0,
                                            cursor: 'pointer',
                                        }}>
                                        <Delete16Regular color="var(--Neutral-Gray-6, #989BAD)" />
                                    </button>
                                </DoubleConfirmationModal>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p className={styles.noMilestonesText}>No Milestones added yet</p>
            )}

            <div className="mb-2">
                <p className={styles.fieldLabel}>Milestone</p>
                <Input className={styles.inputField} value={milestoneDraft.milestoneName} onChange={e => handleDraftChange('milestoneName', e.target.value)} />
            </div>
            <div className="mb-3">
                <p className={styles.fieldLabel}>Date</p>
                <Input
                    className={styles.inputField}
                    style={{ width: '160px' }}
                    type="date"
                    value={milestoneDraft.milestoneDate}
                    onChange={e => handleDraftChange('milestoneDate', e.target.value)}
                />
            </div>
            <div className="d-flex align-items-center justify-content-between mt-5" style={{ gap: 12 }}>
                <button type="button" onClick={handleSave} disabled={!milestoneDraft.milestoneName?.trim() || !milestoneDraft.milestoneDate} className={styles.submitButton}>
                    {editingIndex !== null ? 'Update Milestone' : 'Save Milestone'}
                    {projectDetailButtonLoading && <AiOutlineLoading3Quarters className="ms-2 rotateLoader" />}
                </button>
                {editingIndex !== null && (
                    <span onClick={resetDraft} className={styles.linkBtn}>
                        Cancel
                    </span>
                )}
            </div>
        </div>
    );
};

export default AddMilestones;
